import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Truck, MapPin, ChevronRight, Lock, CheckCircle, Smartphone, Shield } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';

const CheckoutPage = ({ onBack, onNavigate }) => {
  const { t } = useI18n();
  const { cart, cartTotal, clearCart, user } = useApp();
  const [step, setStep] = useState(1); // 1: shipping, 2: payment, 3: confirm
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [shipping, setShipping] = useState({
    name: user?.name || '',
    address: '',
    city: '',
    cap: '',
    phone: ''
  });

  const [payment, setPayment] = useState({
    method: 'card', // card, paypal, applepay
    cardNumber: '',
    expiry: '',
    cvv: '',
    cardName: ''
  });

  const shippingCost = 4.99;
  const total = cartTotal + shippingCost;

  const handleSubmitOrder = async () => {
    setIsProcessing(true);
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const orderId = 'ORD' + Date.now();
    clearCart();
    onNavigate('order-success', { orderId });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32 lg:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 lg:top-[73px]">
        <div className="max-w-screen-2xl mx-auto flex items-center gap-4 px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full lg:hidden">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{t('checkout', 'checkout')}</h1>
        </div>
        
        {/* Progress Steps */}
        <div className="max-w-screen-2xl mx-auto px-6 pb-4">
          <div className="flex items-center justify-between max-w-md mx-auto lg:max-w-lg">
            {[
              { num: 1, label: t('checkout', 'shippingStep') },
              { num: 2, label: t('checkout', 'paymentStep') },
              { num: 3, label: t('checkout', 'confirmStep') }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-sm lg:text-base font-medium transition-all ${
                    step >= s.num ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6" /> : s.num}
                  </div>
                  <span className={`text-xs lg:text-sm mt-1 ${step >= s.num ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
                    {s.label}
                  </span>
                </div>
                {i < 2 && (
                  <div className={`flex-1 h-0.5 mx-2 lg:mx-4 ${step > s.num ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Layout: Two Column */}
      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-4 lg:py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Shipping */}
            {step === 1 && (
              <div className="space-y-4 lg:space-y-6">
                <div className="bg-white rounded-none lg:rounded-2xl p-5 lg:p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4 lg:mb-6">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Truck className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900 lg:text-lg">{t('checkout', 'shippingAddress')}</h2>
                      <p className="text-sm text-gray-500">{t('checkout', 'whereToReceive')}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'fullName')}</label>
                      <input
                        type="text"
                        value={shipping.name}
                        onChange={(e) => setShipping({...shipping, name: e.target.value})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'address')}</label>
                      <input
                        type="text"
                        value={shipping.address}
                        onChange={(e) => setShipping({...shipping, address: e.target.value})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                        placeholder="Via Roma 123"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'city')}</label>
                        <input
                          type="text"
                          value={shipping.city}
                          onChange={(e) => setShipping({...shipping, city: e.target.value})}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                          placeholder="Milano"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'zipCode')}</label>
                        <input
                          type="text"
                          value={shipping.cap}
                          onChange={(e) => setShipping({...shipping, cap: e.target.value})}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                          placeholder="20100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'phone')}</label>
                      <input
                        type="tel"
                        value={shipping.phone}
                        onChange={(e) => setShipping({...shipping, phone: e.target.value})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Options */}
                <div className="bg-white rounded-none lg:rounded-2xl p-5 lg:p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 lg:text-lg">{t('checkout', 'deliveryOptions')}</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-4 p-4 border-2 border-emerald-500 bg-emerald-50 rounded-xl cursor-pointer">
                      <input type="radio" name="delivery" defaultChecked className="text-emerald-500 w-5 h-5" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{t('checkout', 'standard')}</p>
                        <p className="text-sm text-gray-500">3-5 {t('checkout', 'workingDays')}</p>
                      </div>
                      <span className="font-bold text-gray-900">€4,99</span>
                    </label>
                    <label className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors">
                      <input type="radio" name="delivery" className="text-emerald-500 w-5 h-5" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{t('checkout', 'express')}</p>
                        <p className="text-sm text-gray-500">1-2 {t('checkout', 'workingDays')}</p>
                      </div>
                      <span className="font-bold text-gray-900">€9,99</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <div className="space-y-4 lg:space-y-6">
                <div className="bg-white rounded-none lg:rounded-2xl p-5 lg:p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4 lg:mb-6">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900 lg:text-lg">{t('checkout', 'paymentMethod')}</h2>
                      <p className="text-sm text-gray-500">{t('checkout', 'choosePayment')}</p>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div className="space-y-3 mb-6">
                    <label className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      payment.method === 'card' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input 
                        type="radio" 
                        name="payment" 
                        checked={payment.method === 'card'}
                        onChange={() => setPayment({...payment, method: 'card'})}
                        className="text-emerald-500 w-5 h-5" 
                      />
                      <CreditCard className="w-6 h-6 text-gray-600" />
                      <span className="font-medium text-gray-900">{t('checkout', 'creditCard')}</span>
                    </label>
                    <label className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      payment.method === 'paypal' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input 
                        type="radio" 
                        name="payment"
                        checked={payment.method === 'paypal'}
                        onChange={() => setPayment({...payment, method: 'paypal'})}
                        className="text-emerald-500 w-5 h-5" 
                      />
                      <div className="w-6 h-6 bg-blue-600 rounded text-white text-xs font-bold flex items-center justify-center">PP</div>
                      <span className="font-medium text-gray-900">PayPal</span>
                    </label>
                    <label className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      payment.method === 'applepay' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input 
                        type="radio" 
                        name="payment"
                        checked={payment.method === 'applepay'}
                        onChange={() => setPayment({...payment, method: 'applepay'})}
                        className="text-emerald-500 w-5 h-5" 
                      />
                      <Smartphone className="w-6 h-6 text-gray-600" />
                      <span className="font-medium text-gray-900">Apple Pay / Google Pay</span>
                    </label>
                  </div>

                  {/* Card Details */}
                  {payment.method === 'card' && (
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'cardNumber')}</label>
                        <input
                          type="text"
                          value={payment.cardNumber}
                          onChange={(e) => setPayment({...payment, cardNumber: e.target.value})}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                          placeholder="1234 5678 9012 3456"
                          maxLength={19}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'expiry')}</label>
                          <input
                            type="text"
                            value={payment.expiry}
                            onChange={(e) => setPayment({...payment, expiry: e.target.value})}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                            placeholder="MM/AA"
                            maxLength={5}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">CVV</label>
                          <input
                            type="text"
                            value={payment.cvv}
                            onChange={(e) => setPayment({...payment, cvv: e.target.value})}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                            placeholder="123"
                            maxLength={4}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1.5">{t('checkout', 'nameOnCard')}</label>
                        <input
                          type="text"
                          value={payment.cardName}
                          onChange={(e) => setPayment({...payment, cardName: e.target.value})}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                          placeholder="MARIO ROSSI"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500 justify-center">
                  <Shield className="w-4 h-4" />
                  <span>{t('checkout', 'securePaymentSSL')}</span>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 3 && (
              <div className="space-y-4 lg:space-y-6">
                <div className="bg-white rounded-none lg:rounded-2xl p-5 lg:p-6 shadow-sm">
                  <h2 className="font-semibold text-gray-900 mb-4 lg:text-lg">{t('checkout', 'orderSummary')}</h2>
                  
                  {/* Shipping Address */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl mb-4">
                    <MapPin className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{shipping.name}</p>
                      <p className="text-sm text-gray-600">{shipping.address}</p>
                      <p className="text-sm text-gray-600">{shipping.cap} {shipping.city}</p>
                      <p className="text-sm text-gray-600">{shipping.phone}</p>
                    </div>
                    <button onClick={() => setStep(1)} className="text-emerald-600 text-sm font-medium hover:text-emerald-700">
                      {t('checkout', 'edit')}
                    </button>
                  </div>

                  {/* Payment Method */}
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-4">
                    <CreditCard className="w-5 h-5 text-emerald-500" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {payment.method === 'card' ? t('checkout', 'creditCard') : payment.method === 'paypal' ? 'PayPal' : 'Apple Pay'}
                      </p>
                      {payment.method === 'card' && payment.cardNumber && (
                        <p className="text-sm text-gray-600">**** **** **** {payment.cardNumber.slice(-4)}</p>
                      )}
                    </div>
                    <button onClick={() => setStep(2)} className="text-emerald-600 text-sm font-medium hover:text-emerald-700">
                      {t('checkout', 'edit')}
                    </button>
                  </div>

                  {/* Items */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="font-medium text-gray-900 mb-3">{t('checkout', 'products')} ({cart.length})</h3>
                    <div className="space-y-3">
                      {cart.map((item) => (
                        <div key={item.id} className="flex items-center gap-3">
                          <img src={item.image} alt={item.name} className="w-14 h-14 lg:w-16 lg:h-16 object-cover rounded-lg" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{item.name}</p>
                            <p className="text-sm text-gray-500">{t('checkout', 'qty')}: {item.qty}</p>
                          </div>
                          <p className="font-medium text-gray-900">€{(item.price * item.qty).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary - Desktop Sidebar */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-[180px]">
              <h3 className="text-lg font-bold text-gray-900 mb-6">{t('checkout', 'summary')}</h3>
              
              {/* Items Summary */}
              <div className="space-y-3 mb-4 max-h-40 overflow-y-auto">
                {cart.slice(0, 3).map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">{t('checkout', 'qty')}: {item.qty}</p>
                    </div>
                    <p className="text-sm font-medium text-gray-900">€{(item.price * item.qty).toFixed(2)}</p>
                  </div>
                ))}
                {cart.length > 3 && (
                  <p className="text-sm text-gray-500 text-center">+{cart.length - 3} {t('checkout', 'moreProducts')}</p>
                )}
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>{t('cart', 'subtotal')}</span>
                  <span>€{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{t('cart', 'shipping')}</span>
                  <span>€{shippingCost.toFixed(2)}</span>
                </div>
                <div className="h-px bg-gray-200 my-2" />
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>{t('cart', 'total')}</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>

              {step < 3 ? (
                <button 
                  onClick={() => setStep(step + 1)}
                  className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                >
                  {t('checkout', 'continue')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button 
                  onClick={handleSubmitOrder}
                  disabled={isProcessing}
                  className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('checkout', 'processing')}
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      {t('checkout', 'pay')} €{total.toFixed(2)}
                    </>
                  )}
                </button>
              )}
              
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-4">
                <Shield className="w-4 h-4" />
                <span>{t('checkout', 'secureSSL')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Action - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 safe-area-bottom lg:hidden">
        {step < 3 ? (
          <button 
            onClick={() => setStep(step + 1)}
            className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
          >
            {t('checkout', 'continue')}
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <button 
            onClick={handleSubmitOrder}
            disabled={isProcessing}
            className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('checkout', 'processing')}
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                {t('checkout', 'pay')} €{total.toFixed(2)}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
